import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import DialogActions from '@mui/material/DialogActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { QRCodeSVG } from 'qrcode.react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { dialogManager } from '../stores/DialogManager';
import { showArticleInformationStore } from '../stores/ShowArticleInformationStore';
import { layoutInformation } from '../submodules/xbox/utils/Layout';
import { getDialogShareUrl } from '../utils/dialogUtils';

import Dialog from '@egr/xbox/egr-gui-dialogs/utils/DialogOverride';
import { DialogContent } from '@egr/xbox/egr-gui-dialogs/whiteDialog/DialogContent';
import { DialogTitle } from '@egr/xbox/egr-gui-dialogs/whiteDialog/DialogTitle';
import { LinkExpires } from '@egr/xbox/egr-gui-elements/LinkExpires';
import { DialogTransition } from '@egr/xbox/egr-gui-elements/single-components/DialogTransition';
import { makeStyles } from '@egr/xbox/utils/tss-react';

const useShareDialogStyles = makeStyles({ name: 'ShareDialog' })((theme) => ({
    qr: {
        display: 'block',
        marginTop: '4px',
        minWidth: '180px',
        minHeight: '180px',
        cursor: 'pointer',
    },
    text: {
        paddingBottom: theme.spacing(2),
        textAlign: 'left',
        ['> br']: {
            display: 'block',
            content: '""',
            marginTop: theme.spacing(1),
        },
    },
    centerText: {
        textAlign: 'center',
    },
    textWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        paddingLeft: theme.spacing(3),
        alignSelf: 'stretch',
    },
    textWrapperFullscreen: {
        paddingLeft: 0,
        marginTop: theme.spacing(3),
        alignSelf: undefined,
    },
    content: {
        display: 'flex',
        flexDirection: 'row',
        padding: theme.spacing(3),
    },
    contentModal: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
    },
    contentFullscreen: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
}));

export const ShareDialog = () => {
    const { classes, cx } = useShareDialogStyles();
    const { shareDialog } = dialogManager.useState(['shareDialog']);
    const { t } = useTranslation();
    const { showInformation } = showArticleInformationStore.useState(['showInformation']);
    const [informationChecked, setInformationChecked] = React.useState(showInformation);
    const { smallLayout } = layoutInformation.useState(['smallLayout']);

    if (shareDialog == null) {
        return null;
    }
    const { title, url, expiryDate } = shareDialog;

    const shareUrl = getDialogShareUrl(url, informationChecked);

    function onClose(): void {
        if (shareDialog?.onClose) {
            shareDialog.onClose();
        }
    }

    function copy(): void {
        void dialogManager.copyTextToClipboard(shareUrl);
    }

    return (
        <Dialog
            onClose={onClose}
            open={true}
            TransitionComponent={DialogTransition}
            fullScreen={smallLayout}
        >
            <DialogTitle
                back={onClose}
                shouldClose
            >
                {title}
            </DialogTitle>

            <DialogContent
                dividers
                className={cx(classes.content, smallLayout ? classes.contentFullscreen : classes.contentModal)}
            >
                <QRCodeSVG
                    className={classes.qr}
                    value={shareUrl}
                />

                <div className={cx(classes.textWrapper, smallLayout ? classes.textWrapperFullscreen : undefined)}>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: smallLayout ? 'center' : 'flex-start',
                        }}
                    >
                        <Typography className={cx(classes.text, smallLayout ? classes.centerText : undefined)}>
                            {t('Scan the QR code to view this 3D content on a phone or tablet. To share it with others, copy the link below.')}
                        </Typography>
                        {expiryDate && (
                            <Typography className={cx(classes.text, smallLayout && classes.centerText)}>
                                <LinkExpires expiryDate={expiryDate} />
                            </Typography>
                        )}
                    </div>

                    {showInformation && (
                        <FormControlLabel
                            style={{ alignSelf: smallLayout ? 'center' : 'flex-start', marginBottom: '-12px' }}
                            control={
                                <Checkbox
                                    checked={informationChecked}
                                    onChange={(e) => setInformationChecked(e.target.checked)}
                                />
                            }
                            label={t('Include article information')}
                        />
                    )}
                </div>
            </DialogContent>

            <DialogActions>
                <Button onClick={copy}>{t('Copy link')}</Button>
            </DialogActions>
        </Dialog>
    );
};

ShareDialog.displayName = 'ShareDialog';
